import {
  Hunk,
  HunkLine,
  IntraLineChange,
} from 'clerk_common/diff/diffMarkdown';
import clsx from 'clsx';

type UnifiedDiffViewProps = {
  diff: string;
  hunks?: Hunk[];
};

export function UnifiedDiffView({ diff, hunks }: UnifiedDiffViewProps) {
  if (hunks && hunks.length > 0) {
    return (
      <div className="overflow-hidden rounded border border-gray-200">
        {hunks.map((hunk, idx) => (
          <HunkView key={idx} hunk={hunk} />
        ))}
      </div>
    );
  }

  return <PlainDiff diff={diff} />;
}

const LINE_STYLES = {
  delete: { bg: 'bg-red-50', text: 'text-red-700', prefix: '-' },
  insert: { bg: 'bg-green-50', text: 'text-green-700', prefix: '+' },
  keep: { bg: '', text: 'text-gray-600', prefix: ' ' },
} as const;

function HunkView({ hunk }: { hunk: Hunk }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <div className="bg-gray-100 px-3 py-1 font-mono text-xs text-gray-500">
        @@ -{hunk.oldStart},{hunk.oldCount} +{hunk.newStart},{hunk.newCount} @@
      </div>
      <pre className="overflow-x-auto p-0 text-xs leading-relaxed">
        <div className="w-max min-w-full">
          {hunk.lines.map((line, idx) => (
            <HunkLineView key={idx} line={line} />
          ))}
        </div>
      </pre>
    </div>
  );
}

function HunkLineView({ line }: { line: HunkLine }) {
  const style = LINE_STYLES[line.op];
  const intraLineOp = line.op === 'keep' ? undefined : line.op;

  return (
    <div className={clsx('px-3 py-0.5', style.bg, style.text)}>
      <span className="mr-2 select-none opacity-50">{style.prefix}</span>
      {line.intraLine && intraLineOp ? (
        <IntraLineView changes={line.intraLine} op={intraLineOp} />
      ) : (
        renderLineText(line.text)
      )}
    </div>
  );
}

export function IntraLineView({
  changes,
  op,
}: {
  changes: IntraLineChange[];
  op: 'delete' | 'insert';
}) {
  const highlight =
    op === 'delete' ? 'bg-red-200 font-semibold' : 'bg-green-200 font-semibold';

  return (
    <>
      {changes.map((change, i) =>
        change.type === 'equal' ? (
          <span key={i}>{change.text}</span>
        ) : (
          <span key={i} className={highlight}>
            {change.text}
          </span>
        )
      )}
    </>
  );
}

function renderLineText(text: string) {
  return text === '' ? '\u00A0' : text;
}

type DiffLineKind = 'insert' | 'delete' | 'hunk' | 'meta' | 'context';

function classifyLine(line: string): DiffLineKind {
  if (line.startsWith('@@')) return 'hunk';
  if (
    line.startsWith('+++') ||
    line.startsWith('---') ||
    line.startsWith('Index:') ||
    line.startsWith('===')
  ) {
    return 'meta';
  }
  if (line.startsWith('+')) return 'insert';
  if (line.startsWith('-')) return 'delete';
  return 'context';
}

const PLAIN_LINE_CLASSES: Record<DiffLineKind, string> = {
  insert: 'bg-green-50 text-green-700',
  delete: 'bg-red-50 text-red-700',
  hunk: 'bg-gray-100 text-gray-500',
  meta: 'text-gray-400',
  context: 'text-gray-600',
};

function PlainDiff({ diff }: { diff: string }) {
  const lines = diff.split('\n');

  return (
    <pre className="overflow-x-auto rounded border border-gray-200 bg-gray-50 p-0 text-xs leading-relaxed">
      <div className="w-max min-w-full">
        {lines.map((line, idx) => (
          <div
            key={idx}
            className={clsx(
              'px-3 py-0.5',
              PLAIN_LINE_CLASSES[classifyLine(line)]
            )}
          >
            {renderLineText(line)}
          </div>
        ))}
      </div>
    </pre>
  );
}
